import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LihatDetail from 'components/Icons/LihatDetail'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBProfilePic from 'components/PMBProfilePic'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { Can } from 'context/Acl'
import { enqueueSnackbar } from 'notistack'
import { useRef, useState } from 'react'
import {
  ChevronDown,
  Edit2,
  MoreVertical,
  Plus,
  Search,
  Trash2,
} from 'react-feather'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
  UncontrolledDropdown,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_FORM_WAREHOUSE } from 'utils/pathUrl'
import { BUSINESS_WAREHOUSE, MANAGE } from 'utils/subjectActions'
import {
  useDeleteWarehouseMutation,
  useFetchWarehouseQuery,
} from './warehouseApiSlice'
import { setFetchOption, toggleModal } from './warehouseSlice'

const Warehouse = () => {
  const fetchTimeoutRef = useRef(0)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const intl = useIntl()
  const { tag, isOpenModal } = useSelector((s) => s.warehouse)
  let [page, setPage] = useState({ page: tag.page })
  const [loading, setLoading] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [errMsg, setErrMsg] = useState('')
  const [sortColumn, setSortColumn] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [search, setSearch] = useState('')
  const [searchTemp, setSearchTemp] = useState('')
  const [deleteWarehouse, resDeleteWarehouse] = useDeleteWarehouseMutation()
  const { data, isLoading, refetch, isFetching } = useFetchWarehouseQuery(
    {
      page: tag?.page,
      sortby: sortColumn,
      sortdir: sortDirection,
      size: tag?.size,
      s: searchTemp,
    },
    { skip: !page },
  )

  const columns = [
    {
      name: <span>Nama Gudang</span>,
      sortable: true,
      sortField: 'name',
      minWidth: '150px',
      cell: (row) => {
        return <span>{row.name}</span>
      },
    },
    {
      name: <span>Total Jenis Barang</span>,
      minWidth: '120px',
      maxWidth: '150px',
      sortable: true,
      right: true,
      sortField: 'totalInventory',
      cell: (row) => {
        return (
          <span>{newFormatThousandSeparator(row?.totalInventory || 0)}</span>
        )
      },
    },
    {
      name: <span>PIC</span>,
      minWidth: '120px',
      cell: (row) => (
        <div className="d-flex">
          {row?.warehousemember?.map((e, i) => (
            <div
              key={i}
              id={`pic-${e?.id}`}
              style={{ display: 'inline-block', marginRight: '5px' }}
            >
              <PMBProfilePic
                imgSrc={e?.profilePic}
                altText={e?.name}
                name={e?.name}
                avatarSeed={e?.id}
                width={32}
                height={32}
              />
              <PMBTooltip
                placement="top"
                target={`pic-${e?.id}`}
                text={e?.name}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      name: '',
      minWidth: '80px',
      maxWidth: '100px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex gap-3 align-items-center justify-content-end"
            data-testid={`warehouse-table-row-${row?.id}`}
          >
            <LihatDetail
              onClick={() => {
                navigate(`/gudang/${row?.id}`)
                // dispatch(toggleSidebar())
                setSelectedRow(row)
              }}
              id={row?.id}
            />
            <Can I={MANAGE} this={BUSINESS_WAREHOUSE}>
              {row?.id && (
                <UncontrolledDropdown>
                  <DropdownToggle tag="div">
                    <MoreVertical
                      size={20}
                      color="#475467"
                      className="button"
                    />
                  </DropdownToggle>
                  <DropdownMenu container="body">
                    <DropdownItem
                      className="mb-2 mt-2"
                      onClick={() =>
                        navigate(URL_FORM_WAREHOUSE, { state: row })
                      }
                      data-testid={`warehouse-row-edit-${row?.id}`}
                    >
                      <Edit2 size={15} /> Edit
                    </DropdownItem>
                    <DropdownItem
                      className="mb-2 mt-2 text-danger"
                      onClick={() => {
                        dispatch(toggleModal())
                        setSelectedRow(row)
                      }}
                      data-testid={`warehouse-row-delete-${row?.id}`}
                    >
                      <Trash2 size={15} /> Hapus
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </Can>
          </div>
        )
      },
    },
  ]

  const handleSort = (column, direction) => {
    setSortColumn(column.sortField)
    setSortDirection(direction)
  }

  const handleSearch = (e) => {
    setSearch(e)
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    fetchTimeoutRef.current = setTimeout(() => {
      if (e.trim() !== '') {
        setSearchTemp(e)
      } else {
        setSearch('')
        setSearchTemp('')
      }
    }, 1000)
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const result = await deleteWarehouse(id)
      if (result?.error) {
        throw result
      } else {
        enqueueSnackbar(`Gudang berhasil dihapus`, {
          variant: 'success',
        })
        dispatch(toggleModal())
        refetch({
          page: tag?.page,
          sortby: sortColumn,
          sortdir: sortDirection,
        })
      }
    } catch (error) {
      setErrMsg(error?.error?.data?.message ?? error?.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-100 px-3 pb-3">
      {/* <SidebarWarehouse data={selectedRow} /> */}
      <PMBModalRemove
        isOpen={isOpenModal}
        onClose={() => dispatch(toggleModal())}
        onRemoveData={handleDelete}
        dataId={selectedRow?.id}
        isLoading={loading}
        errMsg={errMsg}
      />
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Gudang'} />
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <DataTable
              columns={columns}
              data={data?.items}
              progressPending={isFetching ?? isLoading}
              responsive
              persistTableHead
              defaultSortFieldId={1}
              defaultSortAsc={true}
              sortIcon={<ChevronDown />}
              sortServer
              noDataComponent={
                <p className="my-3 fw-semibold">
                  Belum ada gudang yang didaftarkan
                </p>
              }
              onSort={handleSort}
              pagination
              paginationServer
              paginationComponent={(props) => (
                <PMBPagination
                  rowCount={data?.totalItems || 0}
                  currentPage={tag?.page}
                  rowsPerPage={tag?.size}
                  setCurrentPage={(e) => {
                    dispatch(setFetchOption({ page: e }))
                  }}
                  setRowsPerPage={(e) => {
                    dispatch(setFetchOption({ size: e }))
                  }}
                  props={props}
                />
              )}
              subHeader={true}
              subHeaderComponent={
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Gudang</h5>
                    <Can I={MANAGE} this={BUSINESS_WAREHOUSE}>
                      <CustomButton
                        onClick={() => navigate(URL_FORM_WAREHOUSE)}
                        color="primary"
                        testId="warehouse-add-button"
                      >
                        <Plus size={15} /> Tambah Gudang
                      </CustomButton>
                    </Can>
                  </div>

                  <p className="mb-2">Cari</p>
                  <InputGroup
                    style={{ borderColor: '#D0D5DD' }}
                    className="mb-4"
                  >
                    <InputGroupText
                      style={{
                        backgroundColor: 'white',
                        borderRightColor: 'white',
                      }}
                    >
                      <Search color="#667085" size={16} />
                    </InputGroupText>
                    <Input
                      style={{
                        maxWidth: '346px',
                      }}
                      placeholder="Cari gudang..."
                      onChange={(e) => handleSearch(e.target.value)}
                      data-testid="warehouse-search-input"
                    />
                  </InputGroup>
                </div>
              }
            />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default Warehouse
